import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowUp,
  faClock,
  faPlus,
  faSearch,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { getFirestore } from '@firebase/firestore';
import { app } from '../../firebase.js';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
  writeBatch,
} from '@firebase/firestore';
import swal from 'sweetalert';

const DAY_PATTERN_OPTIONS = [
  'Mon to Frid',
  'Mon to Thur',
  'Mon, Wed & Fri',
  'Tue & Thur',
  'Sat Only',
  'Sun Only',
  'Custom',
];

const formatTimeLabel = (value) => {
  if (!value) return '';

  const [hourText, minuteText] = String(value).split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText || 0);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return value;
  }

  const normalizedHour = hour % 12 || 12;
  const meridian = hour >= 12 ? 'pm' : 'am';

  if (minute === 0) {
    return `${normalizedHour}${meridian}`;
  }

  return `${normalizedHour}:${String(minute).padStart(2, '0')}${meridian}`;
};

const buildTimeSlotLabel = ({ startTime, endTime, dayPattern, customPattern }) => {
  const startLabel = formatTimeLabel(startTime);
  const endLabel = formatTimeLabel(endTime);
  const patternLabel = dayPattern === 'Custom' ? String(customPattern || '').trim() : dayPattern;

  if (!startLabel || !endLabel || !patternLabel) {
    return '';
  }

  return `${startLabel} - ${endLabel}, ${patternLabel}`;
};

const compareByRank = (firstItem, secondItem) => Number(firstItem.rank || 0) - Number(secondItem.rank || 0);

const INITIAL_FORM = {
  startTime: '',
  endTime: '',
  dayPattern: 'Mon to Frid',
  customPattern: '',
};

function SessionsComp() {
  const db = getFirestore(app);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reorderingId, setReorderingId] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [intakes, setIntakes] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [sessionForm, setSessionForm] = useState(INITIAL_FORM);

  const loadPageData = useCallback(async () => {
    setLoading(true);

    try {
      const [intakeSnapshot, sessionSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'Intakes'))),
        getDocs(query(collection(db, 'classSessions'), orderBy('rank'))),
      ]);

      const intakeRows = intakeSnapshot.docs
        .map((intakeDoc) => ({
          id: intakeDoc.id,
          ...intakeDoc.data(),
        }))
        .sort((firstIntake, secondIntake) => {
          const firstCampus = String(firstIntake.Campus || '');
          const secondCampus = String(secondIntake.Campus || '');
          if (firstCampus !== secondCampus) {
            return firstCampus.localeCompare(secondCampus);
          }

          const firstCourse = String(firstIntake.CourseName || '');
          const secondCourse = String(secondIntake.CourseName || '');
          return firstCourse.localeCompare(secondCourse);
        });

      const sessionRows = sessionSnapshot.docs.map((sessionDoc) => ({
        id: sessionDoc.id,
        ...sessionDoc.data(),
      }));

      setIntakes(intakeRows);
      setSessions(sessionRows);
    } catch (error) {
      console.error('Failed to load session settings data:', error);
      swal('Error', 'Failed to load session settings.', 'error');
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  const filteredIntakes = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return intakes;
    }

    return intakes.filter((intake) => {
      const searchBlob = [
        intake.CourseName,
        intake.Campus,
        intake.intakeDate,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchBlob.includes(normalizedSearch);
    });
  }, [intakes, searchTerm]);

  const sessionsByCampus = useMemo(() => {
    return sessions.reduce((accumulator, session) => {
      const campusKey = String(session.campus || '');
      if (!accumulator[campusKey]) {
        accumulator[campusKey] = [];
      }

      accumulator[campusKey].push(session);
      accumulator[campusKey].sort(compareByRank);
      return accumulator;
    }, {});
  }, [sessions]);

  const selectedCampusSessions = selectedIntake
    ? sessionsByCampus[String(selectedIntake.Campus || '')] || []
    : [];

  const openSessionModal = (intake) => {
    setSelectedIntake(intake);
    setSessionForm(INITIAL_FORM);
    setShowModal(true);
  };

  const closeSessionModal = () => {
    setShowModal(false);
    setSelectedIntake(null);
    setSessionForm(INITIAL_FORM);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setSessionForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
  };

  const renumberCampusSessions = useCallback(
    async (campusName, campusSessions) => {
      const batch = writeBatch(db);

      campusSessions.forEach((session, index) => {
        batch.update(doc(db, 'classSessions', session.id), {
          rank: index + 1,
        });
      });

      await batch.commit();

      setSessions((currentSessions) => {
        const otherCampusSessions = currentSessions.filter((session) => session.campus !== campusName);
        const normalizedCampusSessions = campusSessions.map((session, index) => ({
          ...session,
          rank: index + 1,
        }));

        return [...otherCampusSessions, ...normalizedCampusSessions].sort((firstItem, secondItem) => {
          const campusComparison = String(firstItem.campus || '').localeCompare(String(secondItem.campus || ''));
          if (campusComparison !== 0) {
            return campusComparison;
          }

          return compareByRank(firstItem, secondItem);
        });
      });
    },
    [db]
  );

  const handleSaveSession = async (event) => {
    event.preventDefault();

    if (!selectedIntake) {
      return;
    }

    const nextTimeSlot = buildTimeSlotLabel(sessionForm);

    if (!sessionForm.startTime || !sessionForm.endTime || sessionForm.startTime >= sessionForm.endTime || !nextTimeSlot) {
      swal('Missing details', 'Please select a valid start time, end time, and day pattern.', 'warning');
      return;
    }

    const campusName = String(selectedIntake.Campus || '');
    const duplicateSession = selectedCampusSessions.some(
      (session) => String(session.timeSlot || '').trim().toLowerCase() === nextTimeSlot.toLowerCase()
    );

    if (duplicateSession) {
      swal('Duplicate session', 'That session time already exists for this campus.', 'warning');
      return;
    }

    setSaving(true);

    try {
      const newSessionRef = doc(collection(db, 'classSessions'));
      const provisionalRank = selectedCampusSessions.length + 1;

      await setDoc(newSessionRef, {
        campus: campusName,
        rank: provisionalRank,
        timeSlot: nextTimeSlot,
        docID: newSessionRef.id,
      });

      await renumberCampusSessions(campusName, [
        ...selectedCampusSessions,
        {
          id: newSessionRef.id,
          campus: campusName,
          rank: provisionalRank,
          timeSlot: nextTimeSlot,
          docID: newSessionRef.id,
        },
      ]);

      swal('Saved', 'Session added successfully.', 'success');
      setSessionForm(INITIAL_FORM);
    } catch (error) {
      console.error('Failed to save class session:', error);
      swal('Error', 'Failed to save the class session.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!selectedIntake) {
      return;
    }

    const sessionToDelete = selectedCampusSessions.find((session) => session.id === sessionId);
    if (!sessionToDelete) {
      return;
    }

    const confirmDelete = await swal({
      title: 'Delete session?',
      text: `Remove ${sessionToDelete.timeSlot}?`,
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    setDeletingId(sessionId);

    try {
      await deleteDoc(doc(db, 'classSessions', sessionId));
      const remainingSessions = selectedCampusSessions.filter((session) => session.id !== sessionId);
      await renumberCampusSessions(String(selectedIntake.Campus || ''), remainingSessions);
    } catch (error) {
      console.error('Failed to delete class session:', error);
      swal('Error', 'Failed to delete the class session.', 'error');
    } finally {
      setDeletingId('');
    }
  };

  const handleMoveSession = async (sessionId, direction) => {
    if (!selectedIntake) {
      return;
    }

    const currentIndex = selectedCampusSessions.findIndex((session) => session.id === sessionId);
    if (currentIndex === -1) {
      return;
    }

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= selectedCampusSessions.length) {
      return;
    }

    const reorderedSessions = [...selectedCampusSessions];
    const [movedSession] = reorderedSessions.splice(currentIndex, 1);
    reorderedSessions.splice(targetIndex, 0, movedSession);

    setReorderingId(sessionId);

    try {
      await renumberCampusSessions(String(selectedIntake.Campus || ''), reorderedSessions);
    } catch (error) {
      console.error('Failed to reorder class sessions:', error);
      swal('Error', 'Failed to reorder the class sessions.', 'error');
    } finally {
      setReorderingId('');
    }
  };

  return (
    <div className="clientsComp">
      <div className="dataCompTop">
        <h5>
          Session Settings <span className="badge text-bg-warning">{intakes.length}</span>
        </h5>

        <div className="searchContainer">
          <div className="filterBar">
            <div className="filterInput input-group">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faSearch} />
              </span>
              <Form.Control
                type="text"
                placeholder="Search course, campus, intake date..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="spinner-container">
          <Spinner animation="border" role="status" />
          <span>Loading intakes...</span>
        </div>
      ) : (
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Intake Date</th>
              <th>Course</th>
              <th>Campus</th>
              <th>Saved Sessions</th>
              <th>Add Time</th>
            </tr>
          </thead>
          <tbody>
            {filteredIntakes.length === 0 ? (
              <tr>
                <td colSpan={5}>No intakes found.</td>
              </tr>
            ) : (
              filteredIntakes.map((intake) => {
                const campusSessions = sessionsByCampus[String(intake.Campus || '')] || [];

                return (
                  <tr key={intake.id}>
                    <td>{intake.intakeDate || '—'}</td>
                    <td>{intake.CourseName || '—'}</td>
                    <td>{intake.Campus || '—'}</td>
                    <td>{campusSessions.length}</td>
                    <td>
                      <Button variant="primary" size="sm" onClick={() => openSessionModal(intake)}>
                        <FontAwesomeIcon icon={faPlus} /> Add Session
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={closeSessionModal} centered size="lg">
        <Form onSubmit={handleSaveSession}>
          <Modal.Header closeButton>
            <Modal.Title>Session Settings</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <strong>{selectedIntake?.CourseName || 'Selected intake'}</strong>
              <div>{selectedIntake?.Campus || 'Campus not set'} • {selectedIntake?.intakeDate || 'No intake date'}</div>
            </div>

            <div className="row g-3">
              <div className="col-md-3">
                <Form.Label>Next Rank</Form.Label>
                <div className="form-control bg-light">{selectedCampusSessions.length + 1}</div>
              </div>
              <div className="col-md-3">
                <Form.Label>Start Time</Form.Label>
                <Form.Control
                  type="time"
                  name="startTime"
                  value={sessionForm.startTime}
                  onChange={handleFormChange}
                  disabled={saving}
                />
              </div>
              <div className="col-md-3">
                <Form.Label>End Time</Form.Label>
                <Form.Control
                  type="time"
                  name="endTime"
                  value={sessionForm.endTime}
                  onChange={handleFormChange}
                  disabled={saving}
                />
              </div>
              <div className="col-md-3">
                <Form.Label>Days</Form.Label>
                <Form.Select
                  name="dayPattern"
                  value={sessionForm.dayPattern}
                  onChange={handleFormChange}
                  disabled={saving}
                >
                  {DAY_PATTERN_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </div>
              {sessionForm.dayPattern === 'Custom' && (
                <div className="col-12">
                  <Form.Label>Custom Days</Form.Label>
                  <Form.Control
                    type="text"
                    name="customPattern"
                    placeholder="e.g. Mon to Sat"
                    value={sessionForm.customPattern}
                    onChange={handleFormChange}
                    disabled={saving}
                  />
                </div>
              )}
              <div className="col-12">
                <Form.Label>Preview</Form.Label>
                <div className="form-control bg-light">
                  {buildTimeSlotLabel(sessionForm) || 'Pick a time range to preview the session'}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <h6>Existing Sessions For {selectedIntake?.Campus || 'This Campus'}</h6>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Time Slot</th>
                    <th>Order</th>
                    <th>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedCampusSessions.length === 0 ? (
                    <tr>
                      <td colSpan={4}>No sessions saved for this campus yet.</td>
                    </tr>
                  ) : (
                    selectedCampusSessions.map((session, index) => (
                      <tr key={session.id}>
                        <td>{session.rank}</td>
                        <td>{session.timeSlot}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={index === 0 || reorderingId === session.id}
                              onClick={() => handleMoveSession(session.id, 'up')}
                              type="button"
                            >
                              <FontAwesomeIcon icon={faArrowUp} />
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={index === selectedCampusSessions.length - 1 || reorderingId === session.id}
                              onClick={() => handleMoveSession(session.id, 'down')}
                              type="button"
                            >
                              <FontAwesomeIcon icon={faArrowDown} />
                            </Button>
                          </div>
                        </td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            disabled={deletingId === session.id}
                            onClick={() => handleDeleteSession(session.id)}
                            type="button"
                          >
                            {deletingId === session.id ? (
                              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            ) : (
                              <FontAwesomeIcon icon={faTrash} />
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="dark" onClick={closeSessionModal} type="button" disabled={saving}>
              Close
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  <span> Saving...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faClock} /> Save Session
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default SessionsComp;
